/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import i18n from 'i18n';
import {
  Button as MaterialButton,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Add as AddIcon,
  AlarmAdd as AlarmAddIcon,
  AlarmOff as AlarmOffIcon,
  Security as SecurityIcon,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import Button from 'components/Button';
import DatePicker from 'components/DatePicker';
import Permission from 'components/ValidPermission';
import {
  EnvironmentFields,
  InstanceStateEnum,
  PublishImageEnum,
  ServerFields,
} from 'modules/environments/interfaces';
import { connect } from 'react-redux';
import {
  attachServerRequest,
  stopTimeRequest,
} from 'modules/environments/actions';
import { modalOpen, modalClose } from 'modules/modal/actions';
import ApplicationState from 'store/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import { AttachServerRequestProps } from 'modules/environments/requestInterfaces';
import { useParams } from 'react-router';
import clsx from 'clsx';
import { RolesEnum, PermissionsData } from 'modules/authenticate/interfaces';
import moment from 'moment';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { isClientOperator } from 'utils/role';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import {
  formatDateWithMoment,
  formatDateToISO,
  applyI18n,
} from 'utils/helpers';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import FormPreparationServer from '../FormPreparationServer';
import styles from '../../styles';
import StatusInstance, { getStatusText } from '../../status';
import serverFields from '../serverFields';
import StatusInstanceServer, {
  getStatusText as getStatusTextServer,
} from '../../statusServer';

const useStyles = makeStyles(styles);

interface PreparationServerFieldsProps {
  environmentSelected: EnvironmentFields | null;
  isLoadingPreparationServer: boolean;
  permissions?: PermissionsData;
  preparationServer: ServerFields;
  role?: RolesEnum;
  attachServerRequest: (props: AttachServerRequestProps) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  stopTimeRequest: ({ serverId, environmentUuid, values }) => void;
  setCurrentTab: React.Dispatch<React.SetStateAction<number>>;
  setHighlightSection: React.Dispatch<React.SetStateAction<string>>;
  setPublicationTab: React.Dispatch<React.SetStateAction<string>>;
}

const PreparationServerFields = ({
  environmentSelected,
  isLoadingPreparationServer,
  permissions,
  preparationServer,
  role,
  attachServerRequest,
  modalClose,
  modalOpen,
  stopTimeRequest,
  setCurrentTab,
  setHighlightSection,
  setPublicationTab,
}: PreparationServerFieldsProps) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();

  const handleAttachPreparationServer = () =>
    modalOpen({
      title: i18n.t('ADD_SERVER_PREPARATION_KEY'),
      subtitle: i18n.t('ADD_SERVER_PREPARATION_SUBTITLE_KEY', {
        environment: environmentSelected?.name,
      }),
      content: (
        <FormPreparationServer
          isLoading={isLoadingPreparationServer}
          onCancel={modalClose}
          onSubmit={values => {
            attachServerRequest({
              environmentUuid: id,
              values,
              type: 'preparation',
            });
            modalClose();
          }}
        />
      ),
    });

  const handleNewScheduleStop = (value: ParsableDate) =>
    modalOpen({
      title: i18n.t('NEW_SCHEDULED_STOP_KEY'),
      content: (
        <Typography className={classes.messageStopServer}>
          {i18n.t('ENVIRONMENTS_PAGE.WANT_SCHEDULE_NEW_STOP_TIME_MSG_KEY', {
            time: formatDateWithMoment(value),
          })}
        </Typography>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              const newStopTime = formatDateToISO(value);

              stopTimeRequest({
                environmentUuid: id,
                serverId: preparationServer.instance_id,
                values: {
                  stop_time: newStopTime,
                  disable_auto_stop: false,
                },
              });
              modalClose();
            }}
          >
            {i18n.t('CONFIRM_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleCancelShutdown = () =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WANT_REMOVE_SHUTDOWN_TITLE_KEY'),
      content: (
        <Typography className={classes.messageStopServer}>
          {i18n.t('ENVIRONMENTS_PAGE.WANT_REMOVE_SHUTDOWN_MSG_KEY')}{' '}
          {formatDateWithMoment(preparationServer?.stop_time)}
        </Typography>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              stopTimeRequest({
                environmentUuid: id,
                serverId: preparationServer?.instance_id,
                values: {
                  stop_time: null,
                  disable_auto_stop: true,
                },
              });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleExtendServer = () =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WANT_EXTEND_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.WANT_EXTEND_SERVER_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              stopTimeRequest({
                environmentUuid: id,
                serverId: preparationServer?.instance_id,
                values: {
                  stop_time: null,
                  disable_auto_stop: false,
                },
              });
              modalClose();
            }}
          >
            {i18n.t('EXTEND_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  return !preparationServer ? (
    <Grid container alignItems="center" justify="center">
      <Permission
        value="attach_preparation_server"
        fallback={
          <Alert severity="info" color="info">
            {i18n.t('ENVIRONMENTS_PAGE.NO_PREPARATION_SERVER_KEY')}
          </Alert>
        }
      >
        <MaterialButton
          id="btn-add-server-preparation"
          className={classes.addServerButton}
          startIcon={<AddIcon />}
          onClick={() => handleAttachPreparationServer()}
        >
          {i18n.t('ADD_KEY')}
        </MaterialButton>
      </Permission>
    </Grid>
  ) : (
    <>
      {!isClientOperator(role) && (
        <GenericForm
          fields={preparationServer}
          screenFields={applyI18n([serverFields[0]], i18n.language)}
          errors={[]}
        />
      )}
      <GenericForm
        fields={preparationServer}
        screenFields={applyI18n([serverFields[1]], i18n.language)}
        errors={[]}
      />

      <Grid item xs={6} md={3} style={{ display: 'flex', gap: '8px' }}>
        <GenericForm
          fields={preparationServer}
          screenFields={applyI18n(serverFields.slice(2, 4), i18n.language)}
          errors={[]}
        />
      </Grid>

      {!isClientOperator(role) && (
        <GenericForm
          fields={{
            ...preparationServer,
            flavor: getFlavorDisplayName(preparationServer?.flavor),
          }}
          screenFields={applyI18n([serverFields[4]], i18n.language)}
          errors={[]}
        />
      )}

      <Grid item style={{ display: 'flex', gap: '8px' }} xs={6} md={6}>
        <TextField
          id="platform_version"
          fullWidth
          label={i18n.t('PLATFORM_VERSION_NUMBER_KEY')}
          variant="outlined"
          value={
            preparationServer?.platform_version || i18n.t('NOT_IDENTIFIED_KEY')
          }
          className={classes.platformVersionText}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <Grid
                item
                className={clsx(
                  classes.startAdornment,
                  classes.platformVersionIcon
                )}
              >
                <SecurityIcon />
              </Grid>
            ),
          }}
        />

        <TextField
          id="instance_state"
          fullWidth
          label={i18n.t('ENVIRONMENTS_PAGE.SERVER_STATUS_KEY')}
          variant="outlined"
          value={getStatusTextServer(preparationServer?.instance_state)}
          className={clsx(classes.statusTextField, {
            [classes.runningLabel]:
              preparationServer?.instance_state === InstanceStateEnum.RUNNING,
            [classes.stoppedLabel]:
              preparationServer?.instance_state === InstanceStateEnum.STOPPED,
          })}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <Grid item className={classes.startAdornment}>
                <StatusInstanceServer
                  value={preparationServer?.instance_state}
                  hiddenText
                />
              </Grid>
            ),
          }}
        />
        <TextField
          id="image_publication_status"
          onClick={() => {
            setCurrentTab(1);
            setPublicationTab('');
            setHighlightSection('logs');
          }}
          fullWidth
          label={i18n.t('ENVIRONMENTS_PAGE.IMAGE_STATUS_KEY')}
          variant="outlined"
          style={{ cursor: 'pointer' }}
          value={getStatusText(preparationServer?.image_publication_status)}
          className={clsx(classes.statusTextFieldImage, {
            [classes.runningLabel]:
              preparationServer?.image_publication_status ===
              PublishImageEnum.SUCCESS,
            [classes.stoppedLabel]:
              preparationServer?.image_publication_status ===
                PublishImageEnum.FAILED ||
              preparationServer?.image_publication_status ===
                PublishImageEnum.TIMEOUT,
            [classes.inProgressLabel]:
              preparationServer?.image_publication_status ===
              PublishImageEnum.RUNNING,
            [classes.warningLabel]:
              preparationServer?.image_publication_status ===
              PublishImageEnum.WARNING,
          })}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <Grid item className={classes.startAdornmentImage}>
                <StatusInstance
                  value={preparationServer?.image_publication_status}
                  hiddenText
                />
              </Grid>
            ),
          }}
        />
      </Grid>

      <Grid item xs={6} md={3} className={classes.stopTimeContainer}>
        <Grid container className={classes.stopTimeField}>
          <DatePicker
            id="stop_time"
            type="datetime"
            disabled={
              !permissions?.stop_preparation_server ||
              preparationServer?.block_actions_enabled
            }
            label={i18n.t('SCHEDULED_STOP_KEY')}
            value={preparationServer?.stop_time}
            defaultValue={'N/A'}
            keyValue="stop_time"
            shouldUpdateOnAccept={false}
            setFieldsValue={(_, value) => {
              const time = moment(value);

              if (
                time.isValid() &&
                !time.isSame(preparationServer?.stop_time)
              ) {
                handleNewScheduleStop(value);
              }
            }}
          />
        </Grid>
        <Permission value="stop_preparation_server">
          {!preparationServer?.block_actions_enabled && (
            <Grid item className={classes.alarmIconsContainer}>
              {preparationServer?.stop_time !== 'N/A' && (
                <Tooltip
                  PopperProps={{ disablePortal: true }}
                  title={i18n.t('CANCEL_SCHEDULED_STOP_KEY')}
                >
                  <AlarmOffIcon
                    id="cancel-stop-time"
                    className={classes.cancelStopTimeIcon}
                    onClick={handleCancelShutdown}
                  />
                </Tooltip>
              )}
              <Tooltip
                PopperProps={{ disablePortal: true }}
                title={i18n.t('EXTEND_EXECUTION_KEY')}
              >
                <AlarmAddIcon
                  id="extend-stop-time"
                  className={classes.extendStopTimeIcon}
                  onClick={handleExtendServer}
                />
              </Tooltip>
            </Grid>
          )}
        </Permission>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  environments: {
    environmentSelected,
    preparationServer,
    isLoadingPreparationServer,
  },
}: ApplicationState) => ({
  companySelected,
  environmentSelected,
  isLoadingPreparationServer,
  permissions: data?.permissions,
  preparationServer,
  role: data?.role,
});

export default connect(mapStateToProps, {
  attachServerRequest,
  modalClose,
  modalOpen,
  stopTimeRequest,
})(PreparationServerFields);
